/**
 * What you usually order, remembered without being asked.
 *
 * The alternative was named saved orders — "my usual hot dog" — and it was
 * rejected for the reason every saved-order feature eventually is: it asks the
 * user to do bookkeeping in exchange for a service they only find out about
 * later. Nobody names their coffee. So nothing is named and nothing is managed;
 * the builder simply opens on the version that has been logged most at that
 * chain, and if that stops being true it drifts back on its own.
 *
 * Modal rather than most recent, deliberately. A person who takes a large latte
 * with oat milk four times a week and once orders a small black coffee for
 * someone else should not spend the next visit undoing that. Ties go to the
 * more recent, which is how a genuine change of habit takes over: the new order
 * catches up, then wins.
 *
 * Kept in Dexie's meta table and mirrored in a module variable, because the
 * builder decides its defaults while rendering and an await there would mean a
 * frame of the wrong answer.
 */

import { getMeta, setMeta } from './dexie.js'

export interface UsualOrder {
  /** Which size variant of the item, so a large stays large. */
  itemId: number
  /**
   * Every chosen part as [componentId, count] — two sugars are not one sugar.
   *
   * Absent on orders remembered before counts were kept, which is why
   * `orderComponents` exists rather than reading this field directly.
   */
  components?: Array<[number, number]>
  /** The same choices without their counts. Written for older readers. */
  componentIds: number[]
  /** Generic drink extras, as [key, count] — a Map does not survive JSON. */
  extras: Array<[string, number]>
  cupMl: number | null
  portions: number
}

/**
 * The parts of a remembered order, with how many of each.
 *
 * Only the ids were stored at first, so a coffee with two sugars came back with
 * one and quietly logged 12 kcal short of the habit every morning. An order
 * from before the change reads as one of each, which is what it meant then.
 */
export function orderComponents(order: UsualOrder): Array<[number, number]> {
  return order.components ?? order.componentIds.map((id) => [id, 1])
}

interface Remembered extends UsualOrder {
  /** How many times this exact build has been logged. */
  count: number
  /**
   * When it was last ordered, for breaking ties towards the newer habit.
   *
   * Not `Date.now()`: two orders placed in the same millisecond would tie, and
   * then the tie-break decides nothing. A counter above the wall clock keeps
   * every write strictly ordered while staying comparable across sessions,
   * since the millisecond dominates whenever the two genuinely differ.
   */
  at: number
}

let lastStamp = 0

function stamp(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1)
  return lastStamp
}

const KEY = 'fastfood.usual'

/**
 * How many distinct builds are remembered per menu item. Enough to hold a real
 * preference and its two nearest variations; past that the tail is noise and
 * would keep an order alive that was placed once in March.
 */
const MAX_VARIANTS = 4

type Store = Record<string, Remembered[]>

let store: Store = {}
let hydrated: Promise<void> | null = null
/**
 * Whether anything has been written since the process started.
 *
 * The read from Dexie is asynchronous and the screen is not: an order logged
 * before it lands would otherwise be overwritten by a snapshot taken before it
 * existed. Rare, but the failure is silent and the fix is one flag.
 */
let written = false

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/**
 * React's own way of reading a mutable store outside its tree.
 *
 * The snapshot is the store object itself and it is replaced rather than
 * mutated on every write, so identity comparison is enough for
 * `useSyncExternalStore` and the reading component has a real dependency to
 * name instead of a counter it has to remember to bump.
 */
export function subscribeUsual(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function usualSnapshot(): Store {
  return store
}

/** Awaited once at startup by the screens that read it, then never again. */
export function loadUsualOrders(): Promise<void> {
  hydrated ??= getMeta<Store>(KEY, {}).then((stored) => {
    if (written) return
    store = stored ?? {}
    notify()
  })
  return hydrated
}

void loadUsualOrders()

/**
 * The key an order is remembered under.
 *
 * A `groupKey` where the chain publishes one, because "Latte 0,3 l" and "Latte
 * 0,4 l" are one drink in two cups and remembering them separately would defeat
 * the whole point — the size is part of what is remembered, not part of the
 * question.
 */
export function orderKey(chainSlug: string, groupKey: string | null, itemId: number): string {
  return `${chainSlug}:${groupKey ?? `i${itemId}`}`
}

/** Same build or not. Order-independent, because a Map's order is an accident. */
function signature(order: UsualOrder): string {
  return [
    order.itemId,
    // With the counts, so one sugar and two are two different habits — and
    // spelled the same way for both shapes, so an order remembered before the
    // counts existed still matches the same build ordered again today.
    orderComponents(order)
      .filter(([, count]) => count > 0)
      .sort(([a], [b]) => a - b)
      .map(([id, count]) => `${id}:${count}`)
      .join(','),
    [...order.extras]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, n]) => `${k}:${n}`)
      .join(','),
    order.cupMl ?? '',
    order.portions,
  ].join('|')
}

export async function rememberOrder(key: string, order: UsualOrder): Promise<void> {
  await loadUsualOrders()
  const sig = signature(order)
  const existing = store[key] ?? []
  const match = existing.find((entry) => signature(entry) === sig)

  const next = match
    ? existing.map((entry) =>
        entry === match ? { ...entry, count: entry.count + 1, at: stamp() } : entry,
      )
    : [...existing, { ...order, count: 1, at: stamp() }]

  // Evict the least-used, then the oldest among equals.
  next.sort((a, b) => b.count - a.count || b.at - a.at)
  written = true
  store = { ...store, [key]: next.slice(0, MAX_VARIANTS) }
  notify()
  await setMeta(KEY, store)
}

/** The build to open on, or null when this item has never been ordered. */
export function usualOrder(key: string): UsualOrder | null {
  const entries = store[key]
  if (!entries || entries.length === 0) return null
  const best = [...entries].sort((a, b) => b.count - a.count || b.at - a.at)[0]
  if (!best) return null
  const { count: _count, at: _at, ...order } = best
  return order
}

export interface UsualSummary {
  key: string
  itemId: number
  count: number
  at: number
}

/**
 * The items ordered most at one chain, most-ordered first.
 *
 * Only what has been ordered more than once: a single visit is not a habit, and
 * a "your usual" list that fills up with everything you have ever tried is a
 * second copy of the menu.
 */
export function usualsForChain(chainSlug: string, from: Store = store): UsualSummary[] {
  return Object.entries(from)
    .filter(([key]) => key.startsWith(`${chainSlug}:`))
    .flatMap(([key, entries]) => {
      const count = entries.reduce((sum, entry) => sum + entry.count, 0)
      const best = [...entries].sort((a, b) => b.count - a.count || b.at - a.at)[0]
      if (!best || count < 2) return []
      return [{ key, itemId: best.itemId, count, at: best.at }]
    })
    .sort((a, b) => b.count - a.count || b.at - a.at)
}

/** Test seam: the module variable is otherwise only written through Dexie. */
export function __setUsualStoreForTests(next: Record<string, Remembered[]>): void {
  store = next
  written = false
  hydrated = Promise.resolve()
  notify()
}
